/*
 * InsoleBuddy homepage motion.
 * Vanilla JS, no dependencies. Two responsibilities:
 *  1. Reveal-on-scroll for any [data-ib-reveal] element (adds .is-revealed once).
 *  2. A subtle parallax drift on the hero background.
 * Fully skipped under prefers-reduced-motion.
 */
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveal() {
    var elements = document.querySelectorAll('[data-ib-reveal]');
    if (!elements.length) return;

    // Without a working observer (or with reduced motion), leave content in
    // its default fully-visible state — never stamp the readiness class.
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );

    elements.forEach(function (el) {
      var delay = el.getAttribute('data-ib-reveal-delay');
      if (delay) {
        el.style.setProperty('--ib-reveal-delay', delay + 'ms');
      }
      observer.observe(el);
    });

    // Only NOW that the observer is live may content start hidden: the
    // fail-safe CSS gates all [data-ib-reveal] initial styles behind this
    // class, so if this script never runs, nothing can be stuck invisible.
    document.documentElement.classList.add('ib-motion-ready');
  }

  function initHeroParallax() {
    if (reduceMotion) return;
    var hero = document.querySelector('.ib-hero');
    var media = hero && hero.querySelector('.ib-hero__media');
    if (!media) return;

    var ticking = false;

    function update() {
      var rect = hero.getBoundingClientRect();
      var progress = Math.min(Math.max(-rect.top / (rect.height || 1), 0), 1);
      media.style.transform = 'translateY(' + (progress * 40) + 'px)';
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
  }

  /*
   * Hero background media carousel — implemented as a custom element so it
   * self-initializes on ANY DOM insertion: initial load, theme-editor section
   * re-renders, and Horizon's view-transition page morphs (all of which skip
   * plain inline/DOMContentLoaded scripts and previously left the carousel
   * static). The <ib-carousel-controller> element is rendered inside the hero
   * section; connectedCallback boots it, disconnectedCallback cleans up.
   */
  if (!customElements.get('ib-carousel-controller')) {
    customElements.define(
      'ib-carousel-controller',
      class extends HTMLElement {
        connectedCallback() {
          var hero = this.closest('[data-ib-hero-carousel]');
          if (!hero) return;
          var slides = hero.querySelectorAll('[data-ib-slide]');
          var dots = hero.querySelectorAll('[data-ib-dot]');
          if (slides.length < 2) return;

          this._cleanup();

          var self = this;
          var current = 0;
          var speed = (parseFloat(hero.getAttribute('data-speed')) || 2) * 1000;

          function setActive(index) {
            current = index;
            slides.forEach(function (slide, i) {
              var active = i === index;
              slide.classList.toggle('is-active', active);
              var video = slide.querySelector('video');
              if (!video) return;
              if (active && !reduceMotion) {
                var attempt = video.play();
                if (attempt && attempt.catch) attempt.catch(function () {});
              } else {
                video.pause();
              }
            });
            dots.forEach(function (dot, i) {
              dot.classList.toggle('is-active', i === index);
              if (i === index) {
                dot.setAttribute('aria-current', 'true');
              } else {
                dot.removeAttribute('aria-current');
              }
            });
          }

          function restartTimer() {
            if (self._timer) clearInterval(self._timer);
            // Rotation intentionally runs under prefers-reduced-motion too —
            // CSS disables the crossfade so slides swap instantly; videos stay
            // paused (poster frame shows). Autoplay is a merchant requirement.
            self._timer = setInterval(function () {
              if (document.hidden) return;
              setActive((current + 1) % slides.length);
            }, speed);
          }

          dots.forEach(function (dot, i) {
            dot.addEventListener('click', function () {
              setActive(i);
              restartTimer(); // manual choice shouldn't be instantly overridden
            });
          });

          setActive(0);
          restartTimer();
        }

        disconnectedCallback() {
          this._cleanup();
        }

        _cleanup() {
          if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
          }
        }
      }
    );
  }

  function initLegacyHeroVideo() {
    // Legacy single-video heroes: honor reduced motion
    if (!reduceMotion) return;
    document.querySelectorAll('[data-ib-hero-video] video').forEach(function (video) {
      video.pause();
      video.removeAttribute('autoplay');
    });
  }

  /*
   * Play-nudge for hero slideshow videos. Rotation is pure CSS and does not
   * depend on this — it only kicks browsers that ignored the autoplay
   * attribute (or deprioritized buffering) into actually playing.
   */
  function initHeroVideoNudge() {
    if (reduceMotion) return;
    document.querySelectorAll('.ib-hero__slide video').forEach(function (video) {
      video.muted = true; // property (not just attribute) — required by some browsers
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
      // If it can't play yet (still buffering), try again once it can
      video.addEventListener(
        'canplay',
        function () {
          if (video.paused) {
            var retry = video.play();
            if (retry && retry.catch) retry.catch(function () {});
          }
        },
        { once: true }
      );
    });
  }

  /* Testimonials carousel arrows are handled by inline onclick attributes in
     sections/testimonials.liquid (markup-borne, survives editor re-renders
     and view-transition morphs) — no listener needed here. */

  /*
   * Lazy videos (gallery tiles): preload="none" in markup, so nothing
   * downloads up front. Play only while visible in the viewport; pause when
   * scrolled away (saves bandwidth + battery). Without JS the poster stays.
   */
  function initLazyVideos() {
    var videos = document.querySelectorAll('video[data-ib-lazy-video]');
    if (!videos.length || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting && !reduceMotion) {
            video.muted = true;
            var attempt = video.play();
            if (attempt && attempt.catch) attempt.catch(function () {});
          } else {
            video.pause();
          }
        });
      },
      { rootMargin: '100px 0px' }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });
  }

  /* Bundle add-to-cart is handled by an inline onclick in
     sections/bundle-upsell.liquid (markup-borne, fully reliable) —
     no listener needed here. */

  /*
   * Add-to-cart navigation guard (document-level, capture phase).
   * When Horizon's product-form-component fails to boot (module-loading
   * failures happen in this environment), the ATC form submits natively and
   * the browser lands on /cart/add. This interceptor lives HERE rather than
   * as an inline attribute in blocks/buy-buttons.liquid because the theme
   * editor sync keeps reverting that stock file (it already erased one such
   * guard). Healthy component → we do nothing and its own listener runs the
   * native flow. Dead component → block navigation and replay the flow via
   * AJAX: cart:update event (header bubble) + fly-to-cart when available.
   */
  document.addEventListener(
    'submit',
    function (event) {
      var target = event.target;
      if (!(target instanceof HTMLFormElement)) return;
      if (target.getAttribute('data-type') !== 'add-to-cart-form') return;
      var form = target; // non-null alias: keeps narrowing inside async callbacks

      var component = form.closest('product-form-component');
      if (component && typeof (/** @type {any} */ (component).handleSubmit) === 'function') {
        return; // component is alive — its own listener prevents navigation
      }

      event.preventDefault(); // never allow a raw POST to /cart/add

      var button = form.querySelector('.add-to-cart-button') || form.querySelector('[type=submit]');
      if (button instanceof HTMLButtonElement) button.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return fetch('/cart.js');
        })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: form.id,
                data: { source: 'ib-form-fallback', itemCount: cart.item_count },
              },
            })
          );
          var cartIcon = document.querySelector('.header-actions__cart-icon');
          if (button && cartIcon && customElements.get('fly-to-cart')) {
            var fly = document.createElement('fly-to-cart');
            fly.classList.add('fly-to-cart--main');
            var media = document.querySelector('.product-information__media img');
            if (media instanceof HTMLImageElement) {
              fly.style.setProperty('background-image', 'url(' + media.src + ')');
            }
            fly.style.setProperty('--start-opacity', '0');
            /** @type {any} */ (fly).source = button;
            /** @type {any} */ (fly).destination = cartIcon;
            document.body.appendChild(fly);
          }
          if (button instanceof HTMLButtonElement) button.disabled = false;
        })
        .catch(function () {
          if (button instanceof HTMLButtonElement) button.disabled = false;
        });
    },
    true
  );

  /*
   * Sticky mobile CTA bar: shows after the hero scrolls out of view, hides
   * again near the footer so it never overlaps it. Dismiss is inline in the
   * section markup; this only drives visibility. If this script never runs,
   * the bar stays hidden (it's an enhancement, not content).
   */
  function initStickyCta() {
    var found = document.querySelector('[data-ib-sticky-cta]');
    if (!found) return;
    var bar = found; // non-null alias: keeps narrowing inside closures below

    try {
      if (bar.hasAttribute('data-dismissible') && sessionStorage.getItem('ib-sticky-cta-dismissed') === '1') return;
    } catch (e) { /* private-mode storage errors: just show the bar */ }

    bar.removeAttribute('hidden');

    var pastHero = false;
    var nearFooter = false;

    function render() {
      bar.classList.toggle('is-visible', pastHero && !nearFooter);
    }

    var hero = document.querySelector('.ib-hero');
    if (hero && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { pastHero = !entry.isIntersecting; });
        render();
      }).observe(hero);
    } else {
      window.addEventListener('scroll', function () {
        pastHero = window.scrollY > window.innerHeight * 0.6;
        render();
      }, { passive: true });
    }

    var footer = document.querySelector('.shopify-section-group-footer-group, footer');
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { nearFooter = entry.isIntersecting; });
        render();
      }).observe(footer);
    }
  }

  function initAll() {
    // Isolated so a failure in one initializer can never kill the others
    try { initReveal(); } catch (e) { /* noop */ }
    try { initHeroParallax(); } catch (e) { /* noop */ }
    try { initLegacyHeroVideo(); } catch (e) { /* noop */ }
    try { initHeroVideoNudge(); } catch (e) { /* noop */ }
    try { initLazyVideos(); } catch (e) { /* noop */ }
    try { initStickyCta(); } catch (e) { /* noop */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      document.querySelectorAll('[data-ib-reveal]:not(.is-revealed)').forEach(function (el) {
        el.classList.add('is-revealed');
      });
      initHeroParallax();
    });
  }
})();
